import { useEffect, useMemo, useState } from "react";
import { Printer, ChevronDown, Search, X } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { saveBlobResponse } from "@/utils/download";
import { protocolsApi } from "../api/assetRegistry";
import { useAssignableAssets, useCreateHandoverProtocol } from "../hooks/useAssetRegistry";
import { extractError } from "../utils";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function RequestEquipmentModal({ open, onClose }: Props) {
  const { data: assets = [], isLoading } = useAssignableAssets(open);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");
  const createProtocol = useCreateHandoverProtocol();

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setNote("");
      setFiles([]);
      setError("");
      setDropdownOpen(false);
      setAssetSearch("");
    }
  }, [open]);

  const filteredAssets = useMemo(() => {
    const q = assetSearch.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) => a.name.toLowerCase().includes(q));
  }, [assets, assetSearch]);

  const selectedAssets = useMemo(
    () => assets.filter((a) => selected.has(a.id)),
    [assets, selected],
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function generatePdf() {
    setGenerating(true);
    setError("");
    try {
      const resp = await protocolsApi.generatePdf(Array.from(selected));
      saveBlobResponse(resp, "protokol_przekazania.pdf");
    } catch (e) {
      setError(extractError(e));
    } finally {
      setGenerating(false);
    }
  }

  function submit() {
    if (selected.size === 0) {
      setError("Wybierz co najmniej jeden sprzęt.");
      return;
    }
    if (files.length === 0) {
      setError("Załącz podpisany protokół przekazania.");
      return;
    }
    const fd = new FormData();
    selected.forEach((id) => fd.append("items", id));
    fd.append("document", files[0]);
    if (note.trim()) fd.append("note", note.trim());
    createProtocol.mutate(fd, {
      onSuccess: () => onClose(),
      onError: (e) => setError(extractError(e)),
    });
  }

  return (
    <Modal open={open} onClose={onClose} title="Poproś o sprzęt" size="lg">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Wybierz sprzęt ({selected.size})
          </label>

          {/* Collapsible dropdown trigger */}
          <button
            type="button"
            onClick={() => setDropdownOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          >
            <span className={selected.size === 0 ? "text-[var(--color-text-muted)]" : ""}>
              {selected.size === 0 ? "Wybierz sprzęt…" : `Wybrano: ${selected.size}`}
            </span>
            <ChevronDown
              size={16}
              className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {/* Selected chips (visible also when collapsed) */}
          {selectedAssets.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {selectedAssets.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)]"
                >
                  {a.name}
                  <button
                    type="button"
                    onClick={() => toggle(a.id)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                    aria-label={`Usuń ${a.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Dropdown panel: search + scrollable list */}
          {dropdownOpen && (
            <div className="mt-2 border border-[var(--color-border)]">
              <div className="relative border-b border-[var(--color-border)]">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
                />
                <input
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                  placeholder="Szukaj po nazwie…"
                  autoFocus
                  className="w-full pl-8 pr-3 py-2 text-sm bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] outline-none"
                />
              </div>
              {isLoading ? (
                <div className="py-6 flex justify-center">
                  <Spinner />
                </div>
              ) : filteredAssets.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)] p-3">
                  {assets.length === 0 ? "Brak dostępnych sprzętów do wyboru." : "Brak wyników."}
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto divide-y divide-[var(--color-border)]">
                  {filteredAssets.map((a) => (
                    <label
                      key={a.id}
                      className="flex items-start gap-3 p-2.5 cursor-pointer hover:bg-[var(--color-bg-elevated)]"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(a.id)}
                        onChange={() => toggle(a.id)}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="text-sm text-[var(--color-text-primary)]">{a.name}</span>
                        {a.description && (
                          <span className="block text-xs text-[var(--color-text-muted)]">
                            {a.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <Button variant="warning" size="sm" onClick={generatePdf} loading={generating}>
            <Printer size={14} className="mr-1.5" />
            Generuj protokół
          </Button>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Wygeneruj protokół, podpisz go i załącz poniżej.
          </p>
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Podpisany protokół *
          </label>
          <FileDropZone files={files} onFilesChange={setFiles} accept=".pdf,.jpg,.jpeg,.png" />
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Uwagi
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={createProtocol.isPending}>
            Złóż wniosek
          </Button>
        </div>
      </div>
    </Modal>
  );
}
